import { useDispatch } from 'react-redux';
import { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Stack, StackItem, Tab, Tabs } from '@optimus-web/core';
import { LocalizedPropertyObject, lt, useFeatureFlag, useScope } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import { Service } from '@mews-commander/data-access';
import { useCommanderSelector } from '@mews-commander/utils';
import { BILLING_EXTENT } from '@mews-commander/legacy-entities/src/actions/bills';
import { getBills, getUniqueBillOwnerIdsByBillIds } from '@mews-commander/legacy-entities/src/ducks/bills';
import { refetchBills } from '../../../../billing/ducks/bills';
import { useServicesGetAll } from '../../../queries/useServicesGetAll';
import { useProductsGetAll } from '../../../queries/useProductsGetAll';
import { SubmitHandlers } from '../../../queries/useProductServiceOrdersAdd';
import { useAddProductForm } from '../hooks/useAddProductForm';
import { SettingsTab } from './SettingsTab';
import { ProductsTab } from './ProductsTab';
import { AddProductFormRef } from './AddProductDialog';
import { TotalBarWithData } from './TotalBarWithData';

interface AddProductDialogContentProps {
    currentFormRef: ForwardedRef<AddProductFormRef>;
    accountId: string;
    serviceId: string;
    billId?: string;
    onClose: () => void;
    setLoading: (loading: boolean) => void;
    scopedReservationIds?: string[];
    setDialogName: (names: LocalizedPropertyObject) => void;
}

enum TabState {
    Products = 'Products',
    AdditionalSettings = 'AdditionalSettings',
}

export const AddProductDialogContent = ({
    currentFormRef,
    accountId,
    serviceId,
    billId,
    setLoading,
    onClose,
    setDialogName,
    scopedReservationIds,
}: AddProductDialogContentProps) => {
    const dispatch = useDispatch();

    const [activeTab, setActiveTab] = useState<TabState>(TabState.Products);

    const scope = useScope() || '';

    const bills = useCommanderSelector(getBills);

    const uniqueBillOwners = useCommanderSelector((state) =>
        getUniqueBillOwnerIdsByBillIds(state, { ids: Object.values(bills).map((bill) => bill.id) })
    );

    const newRoutingRulesPOSEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesPOSEnabled);

    const {
        data: { service, businessSegments },
    } = useServicesGetAll(serviceId, { businessSegments: true });
    const { data: productsData } = useProductsGetAll([serviceId]);

    useEffect(() => {
        if (service) {
            setDialogName(service.names);
        }
    }, [service, setDialogName]);

    if (!service || !productsData) {
        return null;
    }

    const formProps = {
        ref: currentFormRef,
        accountId,
        billId,
        service,
        scopedReservationIds,
        onSubmitting: () => setLoading(true),
        onSubmitted: () => setLoading(false),
        onSuccess: () => {
            const billIds: string[] = billId ? [billId] : [];
            let accountIds: string[] = [accountId];

            if (!billId && newRoutingRulesPOSEnabled) {
                // Refetch all bills by ownerIds when adding product to virtual bill
                accountIds = uniqueBillOwners;
            }

            dispatch(
                refetchBills({
                    billIds,
                    scope,
                    accountIds,
                    reservationGroupId: scope,
                    extent: BILLING_EXTENT,
                })
            );

            onClose();
        },
    };

    return (
        <Stack spacing="300" vertical>
            <Tabs
                activeTabValue={activeTab}
                inline
                showDivider
                onTabClick={(e, value) => setActiveTab(value as TabState)}
            >
                <Tab
                    key={TabState.Products}
                    label={lt(TextKey.AddServiceToReservationTabsProducts)}
                    value={TabState.Products}
                    id={TabState.Products}
                />
                <Tab
                    key={TabState.AdditionalSettings}
                    label={lt(TextKey.AddServiceToReservationTabsAdditionalSettings)}
                    value={TabState.AdditionalSettings}
                    id={TabState.AdditionalSettings}
                />
            </Tabs>
            <Form {...formProps}>
                <StackItem>
                    {activeTab === TabState.Products ? (
                        <Stack vertical spacing="300">
                            <ProductsTab
                                accountId={accountId}
                                isVirtualBill={!billId}
                                service={service}
                                productsData={productsData}
                                scopedReservationIds={scopedReservationIds}
                            />
                            <TotalBarWithData service={service} productsData={productsData} />
                        </Stack>
                    ) : (
                        <SettingsTab service={service} businessSegments={businessSegments} />
                    )}
                </StackItem>
            </Form>
        </Stack>
    );
};

interface FormProps
    extends Pick<AddProductDialogContentProps, 'billId' | 'accountId' | 'scopedReservationIds'>,
        SubmitHandlers {
    service: Service;
}

const Form = forwardRef(({ children, ...props }: React.PropsWithChildren<FormProps>, ref) => {
    const { renderFormContent, submit, hasDirtyFields, validate } = useAddProductForm(props);

    useImperativeHandle(
        ref,
        () => ({
            submit,
            hasDirtyFields,
            validate,
        }),
        [submit, hasDirtyFields, validate]
    );

    return renderFormContent(<>{children}</>);
});
